import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft,
  CheckCircle,
  Target,
  BarChart3,
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Lock,
  Calendar,
  Sparkles,
  Building2
} from "lucide-react";

import ScoreDistributionChart from "@/components/ScoreDistributionChart";
import { getScoreBand } from "@/lib/scoreBands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONFIG = {
  calendlyLink: "https://calendly.com/mark-igtms/gtm-clarity-mri-consultation",
  brandGreen: "#07926b",
  brandDark: "#1a1a1a",
};

const PILLAR_ICONS = [Target, TrendingUp, Sparkles, Building2, BarChart3];

export default function PublicScoreView() {
  const { scoreId } = useParams();
  const [loading, setLoading] = useState(true);
  const [scoreData, setScoreData] = useState(null);

  useEffect(() => {
    const fetchScore = async () => {
      if (!scoreId || scoreId === "undefined") {
        toast.error("Invalid score ID");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/public-scores/${scoreId}`);
        setScoreData(response.data);
      } catch (error) {
        toast.error("Failed to fetch score");
      } finally {
        setLoading(false);
      }
    };

    fetchScore();
  }, [scoreId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#07926b] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading score...</p>
        </div>
      </div>
    );
  }

  if (!scoreData) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-gray-700 font-semibold mb-4">Score not found</p>
          <Link to="/">
            <Button style={{ backgroundColor: CONFIG.brandGreen }}>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const teaser = scoreData.teaser_scores || {};
  const scoreBand = getScoreBand(teaser.final_score || 0);

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/history" className="flex items-center gap-2 text-[#07926b] hover:text-[#065f46] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to History</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Score Header */}
        <Card className="p-8 mb-6 text-center">
          <Badge className="mb-4" style={{ backgroundColor: CONFIG.brandGreen }}>
            Revenue Acceleration Score
          </Badge>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {teaser.company_name || "Company"}
          </h1>
          <p className="text-gray-500 mb-6">{teaser.website || scoreData.url}</p>
          
          {/* Big Score Display */}
          <div className="relative w-40 h-40 mx-auto mb-4">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="80" cy="80" r="70"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="12"
              />
              <circle
                cx="80" cy="80" r="70"
                fill="none"
                stroke={scoreBand.color}
                strokeWidth="12"
                strokeDasharray={`${(teaser.final_score / 100) * 440} 440`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold" style={{ color: scoreBand.color }}>
                {teaser.final_score}
              </span>
              <span className="text-gray-500 text-sm">/100</span>
            </div>
          </div>
          
          <p className="text-lg font-medium" style={{ color: scoreBand.color }}>
            {scoreBand.emoji} {scoreBand.label}
          </p>

          <ScoreDistributionChart score={teaser.final_score || 0} className="mt-6 pt-6 border-t border-gray-100 text-left" />
        </Card>

        {/* Prediction Comparison - for sales calls */}
        {scoreData.predicted_score !== undefined && scoreData.predicted_score !== null && (
          <Card className="p-6 mb-6" style={{ 
            backgroundColor: Math.abs(teaser.final_score - scoreData.predicted_score) <= 10 
              ? 'rgba(7, 146, 107, 0.05)' 
              : 'rgba(251, 191, 36, 0.05)',
            borderLeft: `4px solid ${Math.abs(teaser.final_score - scoreData.predicted_score) <= 10 ? CONFIG.brandGreen : '#f59e0b'}`
          }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ 
                backgroundColor: Math.abs(teaser.final_score - scoreData.predicted_score) <= 10 
                  ? 'rgba(7, 146, 107, 0.1)' 
                  : 'rgba(251, 191, 36, 0.1)' 
              }}>
                <Target className="w-5 h-5" style={{ 
                  color: Math.abs(teaser.final_score - scoreData.predicted_score) <= 10 ? CONFIG.brandGreen : '#f59e0b' 
                }} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Self-Assessment vs Reality</h3>
                <p className="text-sm text-gray-500">How the prospect rated themselves</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-2xl font-bold text-gray-900">{scoreData.predicted_score}</p>
                <p className="text-xs text-gray-500 uppercase">Predicted</p>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-2xl">
                  {teaser.final_score > scoreData.predicted_score ? '📈' : 
                   teaser.final_score < scoreData.predicted_score ? '📉' : '🎯'}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <p className="text-2xl font-bold" style={{ color: CONFIG.brandGreen }}>{teaser.final_score}</p>
                <p className="text-xs text-gray-500 uppercase">Actual</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-white rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Gap Analysis:</strong>{' '}
                {Math.abs(teaser.final_score - scoreData.predicted_score) <= 5 && 
                  "Strong self-awareness - they know their GTM position well."}
                {Math.abs(teaser.final_score - scoreData.predicted_score) > 5 && 
                 Math.abs(teaser.final_score - scoreData.predicted_score) <= 15 && 
                  "Moderate gap - some blind spots to discuss."}
                {Math.abs(teaser.final_score - scoreData.predicted_score) > 15 && 
                 teaser.final_score > scoreData.predicted_score && 
                  "They're underselling themselves - opportunity to highlight hidden strengths."}
                {Math.abs(teaser.final_score - scoreData.predicted_score) > 15 && 
                 teaser.final_score < scoreData.predicted_score && 
                  "Overconfidence detected - important gaps to address in the call."}
              </p>
            </div>
          </Card>
        )}

        {/* Company Summary */}
        {teaser.company_summary && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Company Overview</h2>
            <p className="text-gray-600">{teaser.company_summary}</p>
          </Card>
        )}

        {/* Pillar Breakdown */}
        {teaser.pillar_totals && teaser.pillar_totals.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pillar Breakdown</h2>
            <div className="space-y-4">
              {teaser.pillar_totals.map((pillar, index) => {
                const Icon = PILLAR_ICONS[index] || BarChart3;
                const percentage = (pillar.total / pillar.max) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-700">{pillar.name}</span>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: CONFIG.brandGreen }}>
                        {pillar.total}/{pillar.max}
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Strengths & Gaps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {teaser.top_strengths && teaser.top_strengths.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Top Strength
              </h3>
              <ul className="space-y-2">
                {teaser.top_strengths.map((strength, i) => (
                  <li key={i} className="text-gray-600 flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {teaser.top_gaps && teaser.top_gaps.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-amber-500" />
                Top Gap
              </h3>
              <ul className="space-y-2">
                {teaser.top_gaps.map((gap, i) => (
                  <li key={i} className="text-gray-600 flex items-start gap-2">
                    <span className="text-amber-500 mt-1">•</span>
                    {gap}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* Alignment Insight */}
        {teaser.alignment_insight && (
          <Card className="p-6 mb-6 border-l-4" style={{ borderLeftColor: CONFIG.brandGreen }}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{ color: CONFIG.brandGreen }} />
              Strategic Alignment Insight
            </h3>
            <p className="text-gray-600">{teaser.alignment_insight}</p>
          </Card>
        )}

        {/* CTA */}
        <Card className="p-8 text-center bg-gradient-to-br from-gray-50 to-gray-100">
          <Lock className="w-8 h-8 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Want the Full Report?
          </h3>
          <p className="text-gray-600 mb-6">
            Get the complete 25-category breakdown with detailed recommendations.
          </p>
          <a href={CONFIG.calendlyLink} target="_blank" rel="noopener noreferrer">
            <Button size="lg" style={{ backgroundColor: CONFIG.brandGreen }} className="text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Book Your Strategy Call
            </Button>
          </a>
        </Card>

        {/* Metadata */}
        <p className="text-center text-gray-400 text-sm mt-6">
          Analysis Version: {teaser.analysis_version || "v4"} | 
          Created: {new Date(scoreData.created_at).toLocaleDateString()}
        </p>
      </main>
    </div>
  );
}
